/**
 * Warning banner shown when a pending order exists (e.g. failed 3DS redirect).
 * Offers "Complete Payment" or "Start New Order" actions.
 */
import { useTranslation } from 'react-i18next'
import { AlertCircle, ArrowLeft } from 'lucide-react'
import { Button } from '@/components/ui'

interface PendingOrderBannerProps {
  restaurantName: string
  orderNumber?: string | null
  onCompletePayment: () => void
  onStartFresh: () => void
  onBackToMenu: () => void
}

export function PendingOrderBanner({
  restaurantName,
  orderNumber,
  onCompletePayment,
  onStartFresh,
  onBackToMenu,
}: PendingOrderBannerProps) {
  const { t } = useTranslation()

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-6 max-w-2xl">
        <header className="mb-6">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold">{restaurantName}</h1>
            <Button variant="ghost" onClick={onBackToMenu} className="text-sm text-muted-foreground">
              <ArrowLeft className="h-4 w-4 mr-1" />
              {t('common:backToMenu')}
            </Button>
          </div>
        </header>

        <div className="rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/30 p-6 space-y-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-6 w-6 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <div className="space-y-1">
              <h2 className="text-lg font-semibold">{t('common:pendingOrderTitle')}</h2>
              <p className="text-sm text-muted-foreground">
                {orderNumber
                  ? t('common:pendingOrderMessage', { orderNumber: `#${orderNumber}` })
                  : t('common:pendingOrderMessageNoNumber')}
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <Button variant="black" className="flex-1" onClick={onCompletePayment}>
              {t('common:completePayment')}
            </Button>
            <Button variant="outline" className="flex-1" onClick={onStartFresh}>
              {t('common:startNewOrder')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
